/**
 * Application Lifecycle Utilities
 *
 * Utilities for managing application lifecycle events like window unload,
 * visibility changes, etc.
 */

type CleanupCallback = () => void

/**
 * Register cleanup handlers for when the page is about to unload
 *
 * Use this for tasks like:
 * - Clearing sensitive data
 * - Canceling pending requests
 * - Saving state before page closes
 *
 * @param callback Function to execute before unload
 * @returns Cleanup function to remove the listener
 */
export function onBeforeUnload(callback: CleanupCallback): () => void {
  if (typeof window === 'undefined') {
    return () => {} // No-op for SSR
  }

  const handler = () => {
    try {
      callback()
    } catch (error) {
      console.error('Error in beforeunload handler:', error)
    }
  }

  window.addEventListener('beforeunload', handler)

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handler)
  }
}

/**
 * Register handlers for when the page visibility changes
 *
 * Use this for tasks like:
 * - Pausing animations when page is hidden
 * - Refreshing data when page becomes visible
 * - Stopping timers when hidden
 *
 * @param onVisible Function to execute when page becomes visible
 * @param onHidden Function to execute when page becomes hidden
 * @returns Cleanup function to remove the listener
 */
export function onVisibilityChange(
  onVisible?: () => void,
  onHidden?: () => void
): () => void {
  if (typeof document === 'undefined') {
    return () => {} // No-op for SSR
  }

  const handler = () => {
    try {
      if (document.hidden && onHidden) {
        onHidden()
      } else if (!document.hidden && onVisible) {
        onVisible()
      }
    } catch (error) {
      console.error('Error in visibility change handler:', error)
    }
  }

  document.addEventListener('visibilitychange', handler)

  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handler)
  }
}
